/**
 * Centralized error handling for API operations
 * Provides consistent error handling, translation, and toast notifications
 */

import { toast } from "sonner";
import type { UseFormSetError, FieldValues, FieldPath } from "react-hook-form";
import type { QueryClient, QueryKey } from "@tanstack/react-query";

export interface ApiError {
  error: string; // Error message or translation key
  code?: string; // Error code, normalized from the backend's `error_code`
  field?: string; // Field name for field-level errors
  details?: Record<string, unknown>; // Additional error details
  /** Service names attached to RADIOLOGY_QUEUE_REQUIRED. */
  services?: string[];
}

/**
 * Raw JSON body as the Go backend actually sends it. The wire field is
 * `error_code`; `code` is the normalized name used everywhere in the UI.
 */
interface RawApiErrorBody {
  error?: string;
  message?: string;
  error_code?: string;
  code?: string;
  field?: string;
  details?: Record<string, unknown>;
  services?: string[];
}

export interface ErrorHandlerOptions<TFieldValues extends FieldValues = FieldValues> {
  /**
   * Translation function from next-intl
   */
  t: (key: string) => string;

  /**
   * React Hook Form setError function (optional, for form field errors).
   * Generic over the form's field-values type so callers can pass a
   * concretely-typed `form.setError` without a cast.
   */
  setError?: UseFormSetError<TFieldValues>;

  /**
   * Whether to show toast notification (default: true)
   */
  showToast?: boolean;

  /**
   * Custom error message override
   */
  customMessage?: string;

  /**
   * Whether to skip auth errors (default: true)
   */
  skipAuthErrors?: boolean;
}

/**
 * Checks if an error is an authentication error
 */
export function isAuthError(error: Error | null): boolean {
  if (!error) return false;
  return error.message.includes("AUTH_ERROR:");
}

/**
 * Parses error message to extract API error structure
 */
export function parseApiError(error: Error): ApiError | null {
  try {
    const parsed = JSON.parse(error.message) as RawApiErrorBody;
    const message = parsed.error ?? parsed.message;
    if (!message) return null;

    // The backend sends `error_code`; the UI reads `.code`. This used to be a
    // blind `parsed as ApiError` cast, so `.code` was ALWAYS undefined and
    // every `apiCode === "..."` branch silently never fired. Branches with a
    // string fallback still worked by accident, which is why the gap went
    // unnoticed — RADIOLOGY_QUEUE_REQUIRED, the one branch keyed purely on
    // the code, surfaced as a generic "update failed" toast instead of
    // telling the operator to pick a queue.
    return {
      error: message,
      code: parsed.error_code ?? parsed.code,
      field: parsed.field,
      details: parsed.details,
      services: parsed.services,
    };
  } catch {
    // Not JSON. An error raised in the client itself can still carry a code
    // beside a plain message (e.g. SESSION_REFRESH_UNAVAILABLE from
    // nextjs-fetch), so the translated message is used where one exists.
    const errorCode = (error as Error & { errorCode?: unknown }).errorCode;
    if (typeof errorCode === "string" && error.message) {
      return { error: error.message, code: errorCode };
    }
  }
  return null;
}

/**
 * Maps backend error codes to translation keys
 */
export function getErrorTranslationKey(code?: string): string | null {
  if (!code) return null;

  const codeMap: Record<string, string> = {
    VALIDATION_ERROR: "error-validation",
    UNAUTHORIZED: "error-unauthorized",
    FORBIDDEN: "error-forbidden",
    NOT_FOUND: "error-not-found",
    CONFLICT: "error-conflict",
    INTERNAL_ERROR: "error-internal",
    RATE_LIMIT_EXCEEDED: "error-rate-limit",
    // A full dotted key, because callers pass the root useTranslations(). (The
    // error-* values above match no key in messages/.) Thrown by nextjs-fetch
    // when a 401's refresh could not be answered (429, 5xx, network): not an
    // auth error, the session may well be fine.
    SESSION_REFRESH_UNAVAILABLE: "errors.sessionRefreshUnavailable",
    // The server refuses everything until the password is changed. The guard
    // (components/password-change-guard.tsx) moves the tab to the screen; this
    // message covers the moment in between, and any caller that shows the
    // error itself rather than a toast.
    PASSWORD_CHANGE_REQUIRED: "errors.passwordChangeRequired",
    // The phone system: a call flow owns this number / row / menu.
    MANAGED_BY_FLOW: "errors.managedByFlow",
    NUMBER_ON_FLOW: "errors.numberOnFlow",
    PASSWORD_CURRENT_INVALID: "errors.passwordCurrentInvalid",
    PASSWORD_TOO_SHORT: "errors.passwordTooShort",
    PASSWORD_UNCHANGED: "errors.passwordUnchanged",
    // Voicemail inbox and phone settings.
    PBX_VOICEMAIL_UNAVAILABLE: "errors.pbxVoicemailUnavailable",
    PBX_VOICEMAIL_AUDIO_MISSING: "errors.pbxVoicemailAudioMissing",
    PBX_SETTINGS_UNAVAILABLE: "errors.pbxSettingsUnavailable",
    PBX_SETTINGS_SYNC_OFF: "errors.pbxSettingsSyncOff",
    PBX_SETTINGS_SYNC_FAILED: "errors.pbxSettingsSyncFailed",
    PBX_VAR_READ_ONLY: "errors.pbxVarReadOnly",
  };

  return codeMap[code] || null;
}

/**
 * Gets translated error message
 */
export function getTranslatedError(
  error: string,
  t: (key: string) => string,
  code?: string
): string {
  // NEW: Try error code first
  if (code) {
    const translationKey = getErrorTranslationKey(code);
    if (translationKey) {
      return t(translationKey);
    }
  }

  // "error-<entity>-<column>-exists" is the backend's unique-violation key
  // (utils.HandleFieldError). No locale defines one, and a missing key renders
  // "" in production — an empty field message or toast — so every such key
  // shows the generic translated duplicate message.
  if (/^error-.+-exists$/.test(error)) {
    return t("errors.uniqueConstraintViolation");
  }

  // If it's a translation key (starts with "error-"), translate it
  if (error.startsWith("error-")) {
    return t(error);
  }

  // Otherwise, try to map common error patterns
  const errorLower = error.toLowerCase();

  if (errorLower.includes("duplicate") || errorLower.includes("unique")) {
    return t("errors.uniqueConstraintViolation");
  }

  if (errorLower.includes("not found") || errorLower.includes("404")) {
    return t("errors.notFound.title");
  }

  if (errorLower.includes("validation") || errorLower.includes("invalid")) {
    return t("errors.validation.title");
  }

  if (errorLower.includes("unauthorized") || errorLower.includes("401")) {
    return t("errors.unauthorized.title");
  }

  // Return original error if no mapping found
  return error;
}

/**
 * Handles API operation errors with consistent behavior
 */
/**
 * A generic key with no translation (e.g. "error-conflict") renders "" in
 * production; when the server explained itself in details.message, that
 * explanation is better than an empty toast.
 */
function withServerReason(translated: string, details: unknown, code?: string): string {
  // Only a typed refusal (one with an error_code) carries a sentence meant
  // for people; other details.message text is a raw decoder/validator
  // message the backend deliberately keeps out of the UI.
  const reason =
    code && details && typeof details === "object" && typeof (details as { message?: unknown }).message === "string"
      ? (details as { message: string }).message
      : "";
  if (reason && (!translated || translated.startsWith("⟨MISSING") || /^error-[a-z-]+$/.test(translated))) {
    return reason;
  }
  return translated;
}

export function handleApiError<TFieldValues extends FieldValues = FieldValues>(
  error: Error,
  options: ErrorHandlerOptions<TFieldValues>
): void {
  const {
    t,
    setError,
    showToast = true,
    customMessage,
    skipAuthErrors = true,
  } = options;

  // Skip auth errors (they redirect to login)
  if (skipAuthErrors && isAuthError(error)) {
    return;
  }

  // Try to parse as API error
  const apiError = parseApiError(error);

  // A forced password change redirects, exactly like an auth error: the guard
  // is already moving this tab to the change-password screen. A toast on the
  // way out is noise the user cannot act on.
  if (apiError?.code === "PASSWORD_CHANGE_REQUIRED") {
    return;
  }

  if (apiError) {
    // NEW: Handle validation errors with details
    if (
      apiError.code === "VALIDATION_ERROR" &&
      apiError.details &&
      setError
    ) {
      // Set field-level errors from details
      Object.entries(apiError.details).forEach(([field, message]) => {
        setError(field as FieldPath<TFieldValues>, {
          type: "validation",
          message: String(message),
        });
      });

      // Show summary toast (deduped — repeated submits replace in place
      // instead of stacking duplicates).
      if (showToast) {
        const errorMessage =
          customMessage || getTranslatedError(apiError.error, t, apiError.code);
        toast.error(errorMessage, { id: `api-error:${errorMessage}` });
      }
      return;
    }

    // Handle field-level errors
    if (apiError.field && setError) {
      const errorMessage =
        customMessage ||
        withServerReason(getTranslatedError(apiError.error, t, apiError.code), apiError.details, apiError.code);
      setError(apiError.field as FieldPath<TFieldValues>, {
        type: "manual",
        message: errorMessage,
      });
      // Don't show toast for field errors (they're shown on the form)
      return;
    }

    // Handle general API errors
    if (showToast) {
      const errorMessage =
        customMessage ||
        withServerReason(getTranslatedError(apiError.error, t, apiError.code), apiError.details, apiError.code);
      toast.error(errorMessage, { id: `api-error:${errorMessage}` });
    }
    return;
  }

  // Handle non-API errors (fallback)
  if (showToast) {
    const errorMessage = customMessage || getTranslatedError(error.message, t);
    toast.error(errorMessage, { id: `api-error:${errorMessage}` });
  }
}

/**
 * Creates a standardized error handler for React Query mutations
 */
export function createMutationErrorHandler<TFieldValues extends FieldValues = FieldValues>(
  t: (key: string) => string,
  setError?: UseFormSetError<TFieldValues>
) {
  return (error: Error) => {
    handleApiError(error, {
      t,
      setError,
      showToast: true,
      skipAuthErrors: true,
    });
  };
}

/**
 * Gets success message translation key for API operations
 */
export function getSuccessMessageKey(
  operation: "create" | "update" | "delete",
  entity: string
): string {
  // Handle past tense for operations
  const pastTense: Record<string, string> = {
    create: "created",
    update: "updated",
    delete: "deleted",
  };
  return `${entity}-${pastTense[operation]}`;
}

/**
 * Creates a standardized mutation configuration for React Query
 * This helper reduces boilerplate in mutation hooks
 */
export function createApiMutationConfig<
  TData,
  TVariables,
  TFieldValues extends FieldValues = FieldValues,
>(options: {
  entity: string;
  t: (key: string) => string;
  queryClient: QueryClient;
  queryKey: QueryKey;
  mutationFn: (variables: TVariables) => Promise<TData>;
  setError?: UseFormSetError<TFieldValues>;
  onSuccessCallback?: () => void;
}) {
  const {
    entity,
    t,
    queryClient,
    queryKey,
    mutationFn,
    setError,
    onSuccessCallback,
  } = options;

  return {
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      const successKey = getSuccessMessageKey("create", entity);
      // Dedup by mutation key so rapid double-clicks (or retries) replace
      // the previous success toast in place rather than stacking.
      toast.success(t(successKey) || `${entity} operation successful`, {
        id: `mutation-success:${entity}:create`,
      });
      onSuccessCallback?.();
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        setError,
        showToast: !setError, // Don't show toast if we have setError (field errors)
        skipAuthErrors: true,
      });
    },
  };
}
